import { groupSums, metricValue, type MediaMetric } from './aggregate';
import { bucketRows, type DimensionRow } from './dimensions';

export interface BarSegment {
  dimension: string;
  percent: number;
}

export interface BarRow {
  advertiser: string;
  /** Width of the bar relative to the largest brand, as a percentage. */
  trackPercent: number;
  total: number;
  /** Empty under CPM: a rate has no composition to stack. */
  segments: BarSegment[];
}

/** A brand with nothing in range still gets a visible stub rather than an invisible row. */
const MIN_TRACK_PERCENT = 2.5;

export function buildBarRows(
  rows: DimensionRow[],
  advertisers: string[],
  columns: string[],
  metric: MediaMetric,
): BarRow[] {
  const bucketed = bucketRows(rows, columns);
  const byAdvertiser = groupSums(bucketed, (r) => r.advertiser);
  const byAdvertiserDimension = groupSums(bucketed, (r) => `${r.advertiser}|${r.dimension}`);

  const totals = advertisers.map((advertiser) => {
    const summed = byAdvertiser.get(advertiser) ?? { spend: 0, impressions: 0 };
    return metricValue(summed, metric);
  });
  const max = Math.max(...totals, 0);

  return advertisers.map((advertiser, i) => {
    const total = totals[i];
    const trackPercent =
      max > 0 ? Math.max((total / max) * 100, MIN_TRACK_PERCENT) : MIN_TRACK_PERCENT;

    if (metric === 'cpm') return { advertiser, trackPercent, total, segments: [] };

    const own = byAdvertiser.get(advertiser) ?? { spend: 0, impressions: 0 };
    const ownTotal = metricValue(own, metric);
    const segments = columns
      .map((dimension) => {
        const summed = byAdvertiserDimension.get(`${advertiser}|${dimension}`);
        if (!summed) return null;
        const value = metricValue(summed, metric);
        if (value <= 0 || ownTotal <= 0) return null;
        return { dimension, percent: (value / ownTotal) * 100 };
      })
      .filter((segment): segment is BarSegment => segment !== null);

    return { advertiser, trackPercent, total, segments };
  });
}
